import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, RefreshCw, Send, FileText, CreditCard, MessageCircle, Save, Plus, Trash2, ArrowLeft } from "lucide-react";

const STATUSES = ["draft", "ai_processing", "needs_review", "reviewed", "quotation_ready", "quotation_sent", "payment_pending", "payment_received", "in_progress", "completed", "cancelled"];

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

function LineGrid({ lines, setLines, orderType }) {
  const isLab = orderType === "lab_test" || orderType === "full_body_checkup";
  const columns = isLab
    ? ["Test / Package", "Code", "Qty", "MRP", "Disc %", "GST %", "Line Total", "Remarks"]
    : ["Medicine", "Strength", "Form", "Qty", "MRP", "Disc %", "GST %", "Line Total", "Remarks"];
  const setField = (i, field, value) => setLines(lines.map((l, k) => k === i ? { ...l, [field]: value } : l));
  const add = () => setLines([...lines, { kind: isLab ? "lab_test" : "medicine", name: "", quantity: 1, mrp: 0, discount_pct: 0, gst_pct: 12 }]);
  const remove = (i) => setLines(lines.filter((_, k) => k !== i));
  return (
    <div className="space-y-2" data-testid="order-lines-grid">
      <div className="hidden md:grid grid-cols-12 gap-2 text-xs text-slate-500 px-1">
        {columns.map((c) => <div key={c} className={c === "Remarks" ? "col-span-2" : "col-span-1"}>{c}</div>)}
      </div>
      {lines.map((l, i) => (
        <div key={l.id || l._key || `line-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 border rounded-lg p-2" data-testid={`order-line-${i}`}>
          <Input className="md:col-span-2" placeholder={isLab ? "Test name" : "Medicine"} value={l.name || ""} onChange={(e) => setField(i, "name", e.target.value)} data-testid={`line-name-${i}`} />
          {!isLab && <Input className="md:col-span-1" placeholder="Strength" value={l.strength || ""} onChange={(e) => setField(i, "strength", e.target.value)} />}
          {!isLab && <Input className="md:col-span-1" placeholder="Form" value={l.dosage_form || ""} onChange={(e) => setField(i, "dosage_form", e.target.value)} />}
          {isLab && <Input className="md:col-span-1" placeholder="Code" value={l.test_code || l.code || ""} onChange={(e) => setField(i, "test_code", e.target.value)} />}
          <Input className="md:col-span-1" type="number" step="0.01" value={l.quantity ?? 1} onChange={(e) => setField(i, "quantity", parseFloat(e.target.value) || 0)} data-testid={`line-qty-${i}`} />
          <Input className="md:col-span-1" type="number" step="0.01" value={l.mrp ?? 0} onChange={(e) => setField(i, "mrp", parseFloat(e.target.value) || 0)} data-testid={`line-mrp-${i}`} />
          <Input className="md:col-span-1" type="number" step="0.01" value={l.discount_pct ?? 0} onChange={(e) => setField(i, "discount_pct", parseFloat(e.target.value) || 0)} />
          <Input className="md:col-span-1" type="number" step="0.01" value={l.gst_pct ?? 0} onChange={(e) => setField(i, "gst_pct", parseFloat(e.target.value) || 0)} />
          <div className="md:col-span-1 flex items-center text-sm font-medium">{inr(l.line_total)}</div>
          <Input className="md:col-span-2" placeholder="Remarks" value={l.remarks || ""} onChange={(e) => setField(i, "remarks", e.target.value)} />
          <Button variant="ghost" size="sm" className="md:col-span-1" onClick={() => remove(i)} data-testid={`line-remove-${i}`}><Trash2 size={14} /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid="line-add-btn"><Plus size={14} /> Add row</Button>
    </div>
  );
}

function TotalsCard({ totals }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="order-totals">
      <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">Subtotal</div><div className="text-lg font-semibold">{inr(totals?.subtotal)}</div></div>
      <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">Discount</div><div className="text-lg font-semibold">{inr(totals?.discount_total)}</div></div>
      <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">Taxable</div><div className="text-lg font-semibold">{inr(totals?.taxable_amount)}</div></div>
      <div className="border rounded-lg p-3"><div className="text-xs text-slate-500">GST</div><div className="text-lg font-semibold">{inr(totals?.gst_total)}</div></div>
      <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200"><div className="text-xs text-emerald-700">Grand Total</div><div className="text-xl font-bold text-emerald-900" data-testid="order-grand-total">{inr(totals?.grand_total)}</div></div>
    </div>
  );
}

function TimelinePanel({ orderId }) {
  const [events, setEvents] = useState([]);
  const refresh = async () => {
    try { const { data } = await api.get(`/order-mgmt/orders/${orderId}/timeline`); setEvents(data?.events || []); }
    catch { toast.error("Failed to load timeline"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (orderId) refresh(); }, [orderId]);
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Timeline</CardTitle>
        <Button size="sm" variant="outline" onClick={refresh}><RefreshCw size={14} /> Refresh</Button>
      </CardHeader>
      <CardContent className="space-y-2" data-testid="order-timeline">
        {events.length === 0 && <div className="text-sm text-slate-500">No events yet.</div>}
        {events.map((e, i) => (
          <div key={e.id || e.created_at || `evt-${i}`} className="border-l-4 border-emerald-400 pl-3 py-1">
            <div className="text-sm font-medium">{e.event}</div>
            <div className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString()} · {e.actor || "system"}</div>
            {e.remarks && <div className="text-xs mt-0.5">{e.remarks}</div>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [lines, setLines] = useState([]);
  const [status, setStatus] = useState("draft");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const orderType = order?.order_type;
  const rxDocId = order?.rx_document_id;

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/order-mgmt/orders/${id}`);
      setOrder(data);
      setLines((data?.line_items || []).map((l) => ({ ...l })));
      setStatus(data?.status || "draft");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load order"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (id) refresh(); }, [id]);

  const saveLines = async () => {
    setBusy("lines");
    try {
      const { data } = await api.post(`/order-mgmt/orders/${id}/lines`, { lines, remarks });
      setOrder(data); setLines((data?.line_items || []).map((l) => ({ ...l })));
      toast.success("Lines saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };

  const changeStatus = async (next) => {
    setBusy("status");
    try {
      const { data } = await api.post(`/order-mgmt/orders/${id}/status`, { status: next, remarks });
      setOrder(data); setStatus(data?.status);
      toast.success(`Status: ${next}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Status update failed"); }
    finally { setBusy(""); }
  };

  const doAction = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "quotation") await api.post(`/order-mgmt/orders/${id}/quotation`);
      else if (kind === "payment-link") await api.post(`/order-mgmt/orders/${id}/payment-link`, {});
      else if (kind.startsWith("notify:")) await api.post(`/order-mgmt/orders/${id}/notify`, { event: kind.slice(7) });
      await refresh();
      toast.success(`${kind} done`);
    } catch (e) { toast.error(e?.response?.data?.detail || `${kind} failed`); }
    finally { setBusy(""); }
  };

  const rxTabLabel = orderType === "medicine" ? "Prescription (Rx)" : (orderType === "lab_test" || orderType === "full_body_checkup") ? "Lab Tests" : "Service Details";

  return (
    <div className="p-6 space-y-4" data-testid="order-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)}><ArrowLeft size={16} /> Back</Button>
          <div>
            <h1 className="text-2xl font-bold">Order {order?.id}</h1>
            <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
              <Badge variant="outline">{orderType || "—"}</Badge>
              <Badge variant="outline" data-testid="order-status-badge">{status}</Badge>
              <Badge variant="outline">payment: {order?.payment_status || "—"}</Badge>
              <span>{order?.customer_name || order?.name || ""} · {order?.phone || ""}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={changeStatus} disabled={busy === "status"}>
            <SelectTrigger className="w-48" data-testid="order-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh}>{loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Refresh</Button>
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="service" data-testid="tab-service">{rxTabLabel}</TabsTrigger>
          <TabsTrigger value="quotation" data-testid="tab-quotation">Quotation</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment">Payment</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <Card>
            <CardHeader><CardTitle className="text-lg">Order details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Order ID</div><div>{order?.id}</div></div>
              <div><div className="text-xs text-slate-500">Type</div><div>{orderType}</div></div>
              <div><div className="text-xs text-slate-500">Customer</div><div>{order?.customer_name || order?.name || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Phone</div><div>{order?.phone || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Created</div><div>{order?.created_at || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Updated</div><div>{order?.updated_at || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Rx doc</div><div>{rxDocId || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Grand total</div><div className="font-medium">{inr(order?.totals?.grand_total ?? order?.amount)}</div></div>
              <div className="md:col-span-4">
                <div className="text-xs text-slate-500 mb-1">Staff remarks</div>
                <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="order-remarks" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="service">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">{rxTabLabel}</CardTitle>
              <Button size="sm" onClick={saveLines} disabled={busy === "lines"} data-testid="save-lines-btn">
                {busy === "lines" ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save lines
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {rxDocId && <div className="text-sm text-slate-600">Linked prescription: <span className="font-mono">{rxDocId}</span></div>}
              <LineGrid lines={lines} setLines={setLines} orderType={orderType} />
              <TotalsCard totals={order?.totals} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quotation">
          <Card>
            <CardHeader><CardTitle className="text-lg">Quotation / Invoice</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Button onClick={() => doAction("quotation")} disabled={busy === "quotation"} data-testid="gen-quotation-btn">
                  {busy === "quotation" ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />} Generate Quotation
                </Button>
                <Button variant="outline" onClick={() => doAction("notify:quotation")}><MessageCircle size={14} /> WhatsApp quotation</Button>
              </div>
              <div className="text-sm text-slate-500">Latest quotation ID: <span className="font-mono">{order?.latest_quotation_id || "—"}</span></div>
              <div className="text-sm">Quotations issued: {order?.quotations?.length || 0}</div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payment">
          <Card>
            <CardHeader><CardTitle className="text-lg">Payment</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Button onClick={() => doAction("payment-link")} disabled={busy === "payment-link"} data-testid="gen-payment-link-btn">
                  {busy === "payment-link" ? <Loader2 className="animate-spin" size={14} /> : <CreditCard size={14} />} Create Cashfree link
                </Button>
                <Button variant="outline" onClick={() => doAction("notify:payment_link")}><Send size={14} /> Send link on WhatsApp</Button>
                <Button variant="outline" onClick={() => doAction("notify:payment_confirmation")}>Send payment confirmation</Button>
              </div>
              <div className="text-sm text-slate-500">
                <div>Link ID: <span className="font-mono">{order?.payment_link_id || "—"}</span></div>
                <div>Link URL: <span className="font-mono break-all">{order?.payment_link?.url || "—"}</span></div>
                <div>Payment status: <Badge variant="outline">{order?.payment_status || "—"}</Badge></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline"><TimelinePanel orderId={id} /></TabsContent>
      </Tabs>
    </div>
  );
}
